import { Session } from 'types/listings';
import React, { ComponentProps, useMemo, useState } from 'react';
import { CollapsibleResource } from 'components/CollapsibleResource';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PriceTierWithParticipants } from 'components/Booking/PriceTier/types';
import { IPriceTier } from 'types/experience.types';
import { SessionTimeOption } from 'features/Reserve/hooks/useSessionTimeOptions';
import moment from 'moment/moment';
import { DATE_FORMAT, serializeParams, TIME_FORMAT } from 'utils';
import { BOOKING_MODES } from 'types/booking';
import { filterEmpty } from 'actions/helpers';
import useSearchQueryParams from 'hooks/use-search-params';
import { useDispatch } from 'AppProvider';
import {
  integrateSessionParticipants,
  setTierParticipants,
} from 'actions/participants';
import { useTierParticipants } from 'hooks/use-selected-participants';
import { useResourceMapContext } from 'features/Reserve/contexts/ResourceMapContext';
import { analyticsManager } from 'features/analytics';
import {
  ANALYTICS_EVENT,
  ANALYTICS_PROPERTY,
  isContinuousScheduleType,
  ResourceGroupV3,
  type ScheduleType,
} from '@kouto/types';
import { getProductFromResourceGroups } from 'features/analytics/services/analytics.helpers';
import useContinuousStartingPrice from 'features/Reserve/hooks/useContinuousStartingPrice';
import { useCalculateAdvertisedTierPrice } from 'utils/calculateAdvertisedTierPrice';
import type { ITax } from 'types/experience.types';
import useBrandToggleFeature from '../../BrandToggleFeature/use-brand-toggle-feature';
import { SessionDateAndDuration } from '../../CollapsibleResource/CollapsibleResource';

type PriceTier = IPriceTier & {
  id: string;
};

interface ResourceGroupCardProps {
  allSessionTimes: SessionTimeOption[];
  categoryName?: string;
  collectionId?: string | null;
  collectionName?: string;
  group: ResourceGroupV3;
  handleGroupSelect: (groupId: string) => void;
  latest: boolean;
  /** How the group's collection is sold; a flexible or multi-day group advertises its shortest booking. */
  scheduleType?: ScheduleType | null;
  sessions: Session[];
  showTruncatedView?: boolean;
}

export const ResourceGroupCard = ({
  allSessionTimes,
  categoryName,
  collectionId,
  collectionName,
  group,
  handleGroupSelect,
  latest,
  scheduleType,
  sessions,
  showTruncatedView,
}: ResourceGroupCardProps) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const calculateAdvertisedTierPrice = useCalculateAdvertisedTierPrice();

  // A flexible or multi-day group's tier rate is per increment or per day, not something a guest can
  // book, so its card advertises what its shortest booking costs: the rate times the minimum length.
  const continuousPrice = useContinuousStartingPrice({
    group,
    scheduleType,
    spans: sessions,
  });
  const isContinuous = isContinuousScheduleType(scheduleType);
  const continuousInitialPrice = continuousPrice
    ? `${continuousPrice.amount}`
    : '';

  const { resource, ...props } = useMemo(() => {
    const priceTiers = sessions
      .flatMap<PriceTier>((e) => (e ? e.priceTiers ?? [] : []))
      .sort((a, b) => a.price - b.price);
    const startingPrice = priceTiers[0]?.price || 0;
    const advertisedStartingPrice = calculateAdvertisedTierPrice(
      startingPrice,
      (group.experiences[0]?.taxes as unknown as ITax[]) ?? [],
    );
    const displayedCapacity = Number(group.experiences?.[0]?.displayCapacity);
    // Nothing to advertise for a continuous group until its shortest booking has been priced.
    const initialPrice = isContinuous
      ? continuousInitialPrice
      : `${advertisedStartingPrice}`;

    return {
      resource: group.experiences[0],
      resourceImage: group.medias,
      initialPrice,
      capacityLabel:
        displayedCapacity > 0
          ? `${displayedCapacity} ${translate(
              displayedCapacity === 1 ? 'person' : 'people',
            )}`
          : '',
    };
  }, [
    sessions,
    group,
    translate,
    calculateAdvertisedTierPrice,
    isContinuous,
    continuousInitialPrice,
  ]);

  const [participantsPerTier, setParticipantsPerTier] = useState<
    Record<string, number>
  >({});

  const priceTierParticipants = useMemo<PriceTierWithParticipants[]>(() => {
    return (
      sessions[0]?.priceTiers?.map((t) => ({
        ...t,
        participants: participantsPerTier[t.id] ?? 0,
      })) || []
    );
  }, [participantsPerTier, sessions]);

  const onParticipantsChange: ComponentProps<
    typeof CollapsibleResource
  >['onParticipantsChange'] = (tierId, increment) => {
    if (!participantsPerTier[tierId]) {
      participantsPerTier[tierId] = 0;
    }

    participantsPerTier[tierId] += increment;

    setParticipantsPerTier({ ...participantsPerTier });
  };

  const isCartFeatureEnabled = useBrandToggleFeature('shoppingCart');
  const { searchParams } = useSearchQueryParams();
  const [, setSelectedTierCustomers] = useTierParticipants();

  const findFirstAvailableResource = (session: SessionDateAndDuration) =>
    group.experiences.find((experience) =>
      sessions.some(
        (candidate) =>
          candidate.experienceId === experience.id &&
          candidate.startDateTime === session.startDateTime &&
          candidate.duration === session.duration,
      ),
    ) ?? resource;

  const onReserveClick = (session: SessionDateAndDuration) => {
    const reservedResource = findFirstAvailableResource(session);
    const noOfGuests = priceTierParticipants.reduce(
      (sum, data) => sum + data.participants,
      0,
    );
    if (noOfGuests === 0) {
      return;
    }
    const bookingData = {
      mode: BOOKING_MODES.SHARED,
      noOfGuests,
      sessionDate: moment(session.startDateTime).format(DATE_FORMAT),
      sessionTime: moment(session.startDateTime).format(TIME_FORMAT),
      duration: moment.duration(session.duration, 'minutes').toISOString(),
      supportedParticipantsCount: group.experiences.length,
      groupId: group.id,
      ...(isCartFeatureEnabled ? searchParams : {}),
    };

    const participants = priceTierParticipants.flatMap((p) => {
      if (!p.participants) {
        return [];
      }
      return [
        {
          ...p,
          selectedNumber: p.participants,
        },
      ];
    });

    setSelectedTierCustomers({
      selectedParticipants: participants,
      currentExperience: reservedResource.id,
      groupTitle: group.title,
    });

    dispatch(
      integrateSessionParticipants({
        session,
      }),
    );

    dispatch(
      setTierParticipants({
        participants,
        currentExperience: reservedResource.id,
        groupTitle: group.title ?? undefined,
      }),
    );

    if (latest) {
      bookingData.latest = true;
    }
    const filteredBookingData = filterEmpty(bookingData);

    history.push({
      pathname: `/e/${reservedResource.id}/${
        isCartFeatureEnabled ? 'participants' : 'booking'
      }`,
      search: serializeParams(filteredBookingData),
    });
  };

  const { focusResources, unfocusResources } = useResourceMapContext();

  const handleMouseEnter = () => {
    focusResources(group.experiences.map(({ id }) => id));
  };

  const handleMouseLeave = () => {
    unfocusResources(group.experiences.map(({ id }) => id));
  };

  const handleSelection = (groupId: string) => {
    analyticsManager.sendEvent(ANALYTICS_EVENT.CLICK_EXPERIENCE, {
      [ANALYTICS_PROPERTY.ExperienceId]: group?.id,
      [ANALYTICS_PROPERTY.ExperienceTitle]:
        group?.title ?? 'Untitled experience',
      [ANALYTICS_PROPERTY.ExperienceCategory]: categoryName,
      [ANALYTICS_PROPERTY.ListId]: collectionId ?? undefined,
      [ANALYTICS_PROPERTY.ListName]: collectionName,
      [ANALYTICS_PROPERTY.Products]: JSON.stringify(
        getProductFromResourceGroups({
          groups: [group],
          categoryName,
        }),
      ),
    });
    handleGroupSelect(groupId);
  };

  return (
    <CollapsibleResource
      group={group}
      title={group.title}
      allSessionTimes={allSessionTimes}
      description={group.description}
      includedItems={group.included}
      sessions={sessions}
      {...props}
      showTruncatedView={showTruncatedView}
      onReserveClick={onReserveClick}
      priceTiers={priceTierParticipants}
      onParticipantsChange={onParticipantsChange}
      handleGroupSelect={handleSelection}
      onCardClick={
        showTruncatedView ? () => handleSelection(group.id) : undefined
      }
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    />
  );
};
